export type DiscussionCapture = {
  id: string; blob: Blob; startedAt: string; source: 'voice' | 'dictation'; transcript?: string;
};
export type DiscussionDraftMetadata = { requestId: string; transcript: string };
export type DiscussionCaptureUploader = (capture: DiscussionCapture) => Promise<void>;

type Entry = { scope: string; capture: DiscussionCapture; upload: DiscussionCaptureUploader; pending: Promise<void> | null; error: string | null; generation: number };
const entries = new Map<string, Entry>();
let generation = 0;
const listeners = new Set<() => void>();
function notify() { for (const listener of listeners) listener(); }
export function subscribeDiscussionCaptures(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; }
export function pendingDiscussionCaptures(scope: string): DiscussionCapture[] { return [...entries.values()].filter(entry => entry.scope === scope).map(entry => entry.capture); }
export function assertDiscussionCaptureCapacity(scope: string): void {
  if (pendingDiscussionCaptures(scope).length || entries.size >= 8) throw new Error('請先保存未完成嘅錄音，再開咪。Save the pending recording before starting another microphone capture.');
}
export class DiscussionRecordingSaveError extends Error {
  constructor(readonly capture: DiscussionCapture) { super('錄音未保存，請重試保存；未有發送呢段說話。Recording is kept in this browser; retry saving before sending.'); this.name = 'DiscussionRecordingSaveError'; }
}
export class DiscussionNoAudioError extends Error {
  constructor() { super('未捕捉到音訊樣本，呢次冇錄音。No audio samples captured. Retry recording or send the text explicitly.'); this.name = 'DiscussionNoAudioError'; }
}
export function assertDiscussionAudio(capture: DiscussionCapture): void { if (capture.blob.size <= 44) throw new DiscussionNoAudioError(); }
async function attempt(entry: Entry): Promise<void> {
  if (entry.pending) return entry.pending;
  const task = Promise.resolve().then(() => { if (entry.generation !== generation || entries.get(entry.capture.id) !== entry) throw new Error('Recording belongs to a signed-out session.'); return entry.upload(entry.capture); }).then(() => { if (entries.get(entry.capture.id) === entry) entries.delete(entry.capture.id); }, () => { entry.error = 'Recording save failed.'; throw new DiscussionRecordingSaveError(entry.capture); }).finally(() => { entry.pending = null; notify(); });
  entry.pending = task; entry.error = null; notify();
  return task;
}
export function discussionCaptureGeneration(): number { return generation; }
export function saveDiscussionCapture(scope: string, capture: DiscussionCapture, upload: DiscussionCaptureUploader, ownerGeneration = generation): Promise<void> {
  try { assertDiscussionAudio(capture); } catch (problem) { return Promise.reject(problem); }
  if (ownerGeneration !== generation) return Promise.reject(new Error('Recording belongs to a signed-out session.'));
  let entry = entries.get(capture.id);
  if (entry && entry.scope !== scope) return Promise.reject(new Error('Recording does not belong to this discussion.'));
  if (!entry) { entry = { scope, capture, upload, pending: null, error: null, generation }; entries.set(capture.id, entry); }
  return attempt(entry);
}
/** Logout/auth expiry releases retained audio and prevents queued retries using a later login. */
export function clearDiscussionCaptures(): void { generation += 1; entries.clear(); notify(); }
/** Explicit stop/finish/retry reuses the same retained Blob and capture ID. */
export async function flushDiscussionCaptures(scope: string): Promise<void> {
  const owned = [...entries.values()].filter(entry => entry.scope === scope);
  await Promise.all(owned.map(entry => entry.pending ?? attempt(entry)));
  const remaining = [...entries.values()].find(entry => entry.scope === scope);
  if (remaining) throw new DiscussionRecordingSaveError(remaining.capture);
}
export async function retryDiscussionCaptures(scope: string): Promise<void> {
  await Promise.all([...entries.values()].filter(entry => entry.scope === scope).map(attempt));
}

/** Uses the same owned microphone stream as ASR; it never opens another microphone. */
export function startDiscussionPcm(stream: MediaStream): { ready: Promise<void>; stop: () => Promise<Blob> } {
  const Context = window.AudioContext;
  if (!Context) throw new Error('呢個瀏覽器未支援保存討論錄音。Discussion recording is unavailable in this browser.');
  const context = new Context(), source = context.createMediaStreamSource(stream);
  let processor: ScriptProcessorNode, gain: GainNode;
  try { processor = context.createScriptProcessor(2048, 1, 1); gain = context.createGain(); gain.gain.value = 0; source.connect(processor); processor.connect(gain); gain.connect(context.destination); }
  catch (problem) { source.disconnect(); void context.close().catch(() => undefined); throw problem; }
  const samples = new Int16Array(16_000 * 60), ratio = context.sampleRate / 16_000;
  let length = 0, position = 0, last: number | undefined, stopped: Promise<Blob> | null = null;
  processor.onaudioprocess = event => {
    if (stopped || length >= samples.length) return;
    const input = event.inputBuffer.getChannelData(0), joined = new Float32Array(input.length + (last === undefined ? 0 : 1));
    if (last === undefined) joined.set(input); else { joined[0] = last; joined.set(input, 1); }
    while (position < joined.length - 1 && length < samples.length) {
      const index = Math.floor(position), fraction = position - index, value = Math.max(-1, Math.min(1, joined[index] + (joined[index + 1] - joined[index]) * fraction));
      samples[length++] = Math.round(value * (value < 0 ? 32768 : 32767)); position += ratio;
    }
    position -= Math.max(0, joined.length - 1); last = joined.at(-1);
  };
  const ready = context.state === 'suspended' ? context.resume() : Promise.resolve();
  // The caller may cancel during permission/activation; never leave a rejected ready promise unobserved.
  void ready.catch(() => undefined);
  return { ready, stop: () => {
    if (stopped) return stopped;
    processor.onaudioprocess = null;
    for (const node of [source, processor, gain]) { try { node.disconnect(); } catch { /* release remaining nodes */ } }
    const bytes = new ArrayBuffer(44 + length * 2), view = new DataView(bytes);
    const tag = (at: number, text: string) => [...text].forEach((character, index) => view.setUint8(at + index, character.charCodeAt(0)));
    tag(0, 'RIFF'); view.setUint32(4, bytes.byteLength - 8, true); tag(8, 'WAVEfmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true); view.setUint32(24, 16000, true); view.setUint32(28, 32000, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true); tag(36, 'data'); view.setUint32(40, length * 2, true);
    for (let index = 0; index < length; index++) view.setInt16(44 + index * 2, samples[index], true);
    const blob = new Blob([bytes], { type: 'audio/wav' });
    stopped = context.close().catch(() => undefined).then(() => blob);
    return stopped;
  } };
}
